import { PublicLayout } from "@/components/layout/PublicLayout";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { BookOpen, Mic, TrendingUp, Zap, Users, Award, Smartphone, Globe } from "lucide-react";
import { Link } from "react-router-dom";

const Functions = () => {
  return (
    <PublicLayout
      showHero={true}
      heroTitle="Funktionen & Features"
      heroSubtitle="Entdecken Sie alle Möglichkeiten unserer KI-basierten Hörbuch-Erstellungsplattform und maximieren Sie Ihren Erfolg."
      heroVariant="compact"
    >
      {/* Features Overview */}
      <section className="py-20 px-4 bg-gray-50">
        <div className="container mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-6 text-gray-900">
              Alle Funktionen im Überblick
            </h2>
            <p className="text-lg text-gray-600 leading-relaxed max-w-2xl mx-auto">
              Von der Ideenfindung bis zur Veröffentlichung – unsere KI unterstützt Sie bei jedem Schritt.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <Card className="minimal-card hover-lift">
              <CardContent className="p-8">
                <div className="w-12 h-12 bg-gray-900 rounded-xl flex items-center justify-center mb-6">
                  <Zap className="h-6 w-6 text-white" />
                </div>
                <h3 className="text-xl font-bold mb-4 text-gray-900">Blitzschnelle Erstellung</h3>
                <p className="text-gray-600 leading-relaxed mb-4">
                  Erstellen Sie professionelle Hörbücher in nur 7 Minuten mit unserer fortschrittlichen KI-Technologie.
                </p>
                <ul className="text-sm text-gray-500 space-y-2">
                  <li>• Automatische Textgenerierung</li>
                  <li>• Sofortige Sprachsynthese</li>
                  <li>• Qualitätskontrolle in Echtzeit</li>
                </ul>
              </CardContent>
            </Card>

            <Card className="minimal-card hover-lift">
              <CardContent className="p-8">
                <div className="w-12 h-12 bg-gray-900 rounded-xl flex items-center justify-center mb-6">
                  <Mic className="h-6 w-6 text-white" />
                </div>
                <h3 className="text-xl font-bold mb-4 text-gray-900">Natürliche KI-Stimmen</h3>
                <p className="text-gray-600 leading-relaxed mb-4">
                  Über 200 verschiedene Stimmen in mehreren Sprachen für authentische Hörbücher.
                </p>
                <ul className="text-sm text-gray-500 space-y-2">
                  <li>• Emotionale Sprachmodulation</li>
                  <li>• Mehrsprachige Unterstützung</li>
                  <li>• Anpassbare Sprechgeschwindigkeit</li>
                </ul>
              </CardContent>
            </Card>

            <Card className="minimal-card hover-lift">
              <CardContent className="p-8">
                <div className="w-12 h-12 bg-gray-900 rounded-xl flex items-center justify-center mb-6">
                  <TrendingUp className="h-6 w-6 text-white" />
                </div>
                <h3 className="text-xl font-bold mb-4 text-gray-900">SEO-Optimierung</h3>
                <p className="text-gray-600 leading-relaxed mb-4">
                  Automatische Keyword-Analyse und Optimierung für maximale Sichtbarkeit.
                </p>
                <ul className="text-sm text-gray-500 space-y-2">
                  <li>• Trending Topics Analyse</li>
                  <li>• Automatische Beschreibungen</li>
                  <li>• Optimierte Titel-Generierung</li>
                </ul>
              </CardContent>
            </Card>

            <Card className="minimal-card hover-lift">
              <CardContent className="p-8">
                <div className="w-12 h-12 bg-gray-900 rounded-xl flex items-center justify-center mb-6">
                  <Globe className="h-6 w-6 text-white" />
                </div>
                <h3 className="text-xl font-bold mb-4 text-gray-900">Multi-Platform Publishing</h3>
                <p className="text-gray-600 leading-relaxed mb-4">
                  Veröffentlichen Sie auf allen wichtigen Plattformen mit nur einem Klick.
                </p>
                <ul className="text-sm text-gray-500 space-y-2">
                  <li>• Audible Integration</li>
                  <li>• Spotify for Podcasters</li>
                  <li>• Apple Podcasts & Google</li>
                </ul>
              </CardContent>
            </Card>

            <Card className="minimal-card hover-lift">
              <CardContent className="p-8">
                <div className="w-12 h-12 bg-gray-900 rounded-xl flex items-center justify-center mb-6">
                  <Award className="h-6 w-6 text-white" />
                </div>
                <h3 className="text-xl font-bold mb-4 text-gray-900">Cover-Design KI</h3>
                <p className="text-gray-600 leading-relaxed mb-4">
                  Professionelle Cover werden automatisch basierend auf Ihrem Inhalt erstellt.
                </p>
                <ul className="text-sm text-gray-500 space-y-2">
                  <li>• Themen-angepasste Designs</li>
                  <li>• Hochauflösende Grafiken</li>
                  <li>• Genre-spezifische Stile</li>
                </ul>
              </CardContent>
            </Card>

            <Card className="minimal-card hover-lift">
              <CardContent className="p-8">
                <div className="w-12 h-12 bg-gray-900 rounded-xl flex items-center justify-center mb-6">
                  <Users className="h-6 w-6 text-white" />
                </div>
                <h3 className="text-xl font-bold mb-4 text-gray-900">Analytics & Insights</h3>
                <p className="text-gray-600 leading-relaxed mb-4">
                  Detaillierte Analysen zur Performance Ihrer Hörbücher und Optimierungsvorschläge.
                </p>
                <ul className="text-sm text-gray-500 space-y-2">
                  <li>• Echtzeit-Statistiken</li>
                  <li>• Zielgruppen-Analyse</li>
                  <li>• Umsatz-Tracking</li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Workflow Section */}
      <section className="py-20 px-4 bg-white">
        <div className="container mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-6 text-gray-900">
              So funktioniert's
            </h2>
            <p className="text-lg text-gray-600 leading-relaxed max-w-2xl mx-auto">
              In nur 3 einfachen Schritten zum erfolgreichen Hörbuch
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-gray-900 rounded-full flex items-center justify-center mx-auto mb-6">
                <span className="text-white font-bold text-xl">1</span>
              </div>
              <h3 className="text-xl font-bold mb-4 text-gray-900">Thema eingeben</h3>
              <p className="text-gray-600 leading-relaxed">
                Geben Sie einfach Ihr gewünschtes Thema oder eine Idee ein. Unsere KI analysiert sofort die Marktchancen.
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-gray-900 rounded-full flex items-center justify-center mx-auto mb-6">
                <span className="text-white font-bold text-xl">2</span>
              </div>
              <h3 className="text-xl font-bold mb-4 text-gray-900">KI erstellt Hörbuch</h3>
              <p className="text-gray-600 leading-relaxed">
                Unsere fortschrittliche KI erstellt automatisch Inhalt, Sprache, Cover und optimierte Beschreibungen.
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-gray-900 rounded-full flex items-center justify-center mx-auto mb-6">
                <span className="text-white font-bold text-xl">3</span>
              </div>
              <h3 className="text-xl font-bold mb-4 text-gray-900">Automatisch veröffentlichen</h3>
              <p className="text-gray-600 leading-relaxed">
                Mit einem Klick wird Ihr Hörbuch auf allen wichtigen Plattformen veröffentlicht und optimiert.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 bg-gray-50">
        <div className="container mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6 text-gray-900">
            Bereit durchzustarten?
          </h2>
          <p className="text-lg text-gray-600 mb-8 max-w-2xl mx-auto leading-relaxed">
            Erleben Sie die Zukunft des Hörbuch-Publishings in unserer kostenlosen Live-Demonstration.
          </p>
          
          <Button size="lg" className="bg-gray-900 text-white hover:bg-gray-800 px-8 py-4 text-lg font-semibold" asChild>
            <Link to="/publisher-werden">
              Jetzt Live Demo buchen
            </Link>
          </Button>
        </div>
      </section>
    </PublicLayout>
  );
};

export default Functions;